import React, { useState, useEffect } from 'react';
import { useCart } from 'react-use-cart';
import { useNavigate } from 'react-router-dom';
import { GlowingEffect } from '../../../Shared/GlowingEffect/GlowingEffect';
import '../../../Assets/Stylesheets/Pages/MarketingServices/OptionCustomPaid.scss';
import API_BASE_URL from '../../../config/api';

// Import all the platform logos
import googleLogo from '../../../Assets/Images/Google_logo.png';
import youtubeLogo from '../../../Assets/Images/Youtube_logo.png';
import facebookLogo from '../../../Assets/Images/Facebook_logo.png';
import instagramLogo from '../../../Assets/Images/Instagram_logo.png';
import tiktokLogo from '../../../Assets/Images/Tiktok_logo.png';
import xLogo from '../../../Assets/Images/X_logo.png';
import linkedinLogo from '../../../Assets/Images/Linkedin_logo.png';
import pinterestLogo from '../../../Assets/Images/Pinterest_logo.png';
import spotifyLogo from '../../../Assets/Images/Spotify_logo.png';
import snapchatLogo from '../../../Assets/Images/Snapchat_logo.png';
import amazonLogo from '../../../Assets/Images/Amazon_logo.png';
import moreLogo from '../../../Assets/Images/More_logo.png';

// Array to hold the platforms' data
const platforms = [
  { id: 'google', name: 'Google', logo: googleLogo },
  { id: 'youtube', name: 'YouTube', logo: youtubeLogo },
  { id: 'facebook', name: 'Facebook', logo: facebookLogo },
  { id: 'instagram', name: 'Instagram', logo: instagramLogo },
  { id: 'tiktok', name: 'TikTok', logo: tiktokLogo },
  { id: 'x', name: 'X', logo: xLogo },
  { id: 'linkedin', name: 'LinkedIn', logo: linkedinLogo },
  { id: 'pinterest', name: 'Pinterest', logo: pinterestLogo },
  { id: 'spotify', name: 'Spotify', logo: spotifyLogo },
  { id: 'snapchat', name: 'Snapchat', logo: snapchatLogo },
  { id: 'amazon', name: 'Amazon', logo: amazonLogo },
  { id: 'more', name: 'More', logo: moreLogo },
];

const MIN_BUDGET = 500;

export default function OptionCustomPaid() {
  const [selectedPlatforms, setSelectedPlatforms] = useState({});
  const [budgets, setBudgets] = useState({
    google: 0,
    youtube: 0,
    facebook: 0,
    instagram: 0,
    tiktok: 0,
    x: 0,
    linkedin: 0,
    pinterest: 0,
    spotify: 0,
    snapchat: 0,
    amazon: 0,
    more: 0,
  });

  const [isPlatformCollapsed, setIsPlatformCollapsed] = useState(false);
  
  const { addItem } = useCart();
  const navigate = useNavigate();

  const togglePlatformCollapse = () => {
    setIsPlatformCollapsed(!isPlatformCollapsed);
  };

  // Update budgets when platforms are unselected
  useEffect(() => {
    setBudgets((currentBudgets) => {
      const updatedBudgets = { ...currentBudgets };
      Object.keys(updatedBudgets).forEach((platform) => {
        if (!selectedPlatforms[platform]) {
          updatedBudgets[platform] = 0; // Reset to minimum if unselected
        }
      });
      return updatedBudgets;
    });
  }, [selectedPlatforms]);

  
  const [mediaplanFees, setMediaplanFees] = useState(0);

  // Calculate total budget and media planning fees
  const totalBudget = Object.values(budgets).reduce((acc, curr) => acc + curr, 0);

  const fetchMediaPlanningFees = async (totalBudget) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-media-planning-fees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ budget: totalBudget }),
      });
      const data = await response.json();
      setMediaplanFees(data.mediaPlanningFees);
    } catch (error) {
      console.error('Error fetching media planning fees:', error);
    }
  };

  useEffect(() => {
    if (totalBudget > 0) {
      fetchMediaPlanningFees(totalBudget);
    }
  }, [totalBudget]);

  const [budgetError, setBudgetError] = useState("");

  // Function to handle range input changes
  const handleRangeChange = (platform, value) => {
    setBudgets((prevBudgets) => {
      const updatedBudgets = { ...prevBudgets, [platform]: Number(value) };
      const newTotalBudget = Object.values(updatedBudgets).reduce((acc, curr) => acc + curr, 0);
      
      // Check if the new total budget is below the minimum required budget
      if (newTotalBudget < MIN_BUDGET) {
        setBudgetError(`Unfortunately, the minimum Paid Advertisement Budget is $${MIN_BUDGET}.`);
      } else {
        setBudgetError(""); // Clear the error message if the budget is within the valid range
      }
      
      return updatedBudgets;
    });
  };

  const handlePlatformToggle = (platformId) => {
    if (platformId !== 'more') {
      setSelectedPlatforms(prev => {
        const newState = {
          ...prev,
          [platformId]: !prev[platformId],
        };
        
        // If selecting a platform, set initial budget to minimum
        if (newState[platformId]) {
          setBudgets(prevBudgets => ({
            ...prevBudgets,
            [platformId]: MIN_BUDGET
          }));
        }
        
        return newState;
      });
    } else {
      alert("More platforms coming soon!");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const handleAddToCart = () => {
    const selectedPlatformsList = Object.keys(selectedPlatforms)
      .filter((id) => selectedPlatforms[id])
      .map((id) => {
        const platform = platforms.find((p) => p.id === id);
        return `${platform.name} (${formatCurrency(budgets[id])})`;
      })
      .join(', ');

    const item = {
      id: `custom-paid-ads-${Date.now()}`,
      name: 'Custom Paid Ads Campaign',
      platform: 'Multi-Platform',
      price: totalBudget + mediaplanFees,
      description: `Platforms: ${selectedPlatformsList}`,
      type: 'paid-ads-custom'
    };
    addItem(item);
    alert('Item added to cart!');
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  // Platform selector component with multi-select
  const PlatformSelector = ({ platform }) => {
    const isSelected = selectedPlatforms[platform.id];
    
    return (
      <div 
        className={`platform-item ${isSelected ? 'selected-platform' : ''}`}
        onClick={() => handlePlatformToggle(platform.id)}
      >
        <div className="platform-wrapper">
          <div className="platform-container">
            {isSelected ? (
              <div className="static-glow"></div>
            ) : (
              <GlowingEffect
                spread={15}
                glow={true}
                disabled={false}
                proximity={60}
                inactiveZone={0}
                borderWidth={0}
                className="centered-glow"
              />
            )}
            <img 
              src={platform.logo} 
              alt={`${platform.name} Logo`} 
              className="platform-logo" 
            />
          </div>
          <p className={isSelected ? 'selected-name' : ''}>{platform.name}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="pacp-option-custom-container">
      <button onClick={togglePlatformCollapse} className="collapse-toggle">
        {isPlatformCollapsed ? 'Change Selection' : 'Hide Platform Selection'}
      </button>
      {!isPlatformCollapsed && (
        <div className="pacp-platform-grid-container">
          {platforms.map((platform) => (
            <PlatformSelector key={platform.id} platform={platform} />
          ))}
        </div>
      )}

      {selectedPlatforms['more'] && (
        <div className="more-platforms-message">
          <p>More Platforms coming soon</p>
        </div>
      )}

      {budgetError && (
        <div className="pacp-error-container">
          <p className="pacp-error-message">{budgetError}</p>
        </div>
      )}

      {Object.keys(selectedPlatforms).filter((id) => selectedPlatforms[id]).length > 0 && (
        <div className="pacp-sliders-section">
          <h3 className="pacp-sliders-title">Set Your Budget</h3>
          <div className="pacp-sliders-container">
            {Object.keys(selectedPlatforms).filter((id) => selectedPlatforms[id]).map((platformId) => {
              const platform = platforms.find((p) => p.id === platformId);
              return (
                <div key={platformId} className="pacp-slider-card">
                  <div className="pacp-slider-header">
                    <img src={platform.logo} alt={platform.name} className="pacp-slider-logo" />
                    <span className="pacp-slider-platform-name">{platform.name}</span>
                  </div>
                  <div className="pacp-slider-value">{formatCurrency(budgets[platformId] || 0)}</div>
                  <input
                    type="range"
                    id={`${platformId}-budget`}
                    min="100"
                    max="25000"
                    value={budgets[platformId] || 500}
                    onChange={(e) => handleRangeChange(platformId, e.target.value)}
                    className="pacp-slider"
                    step="100"
                  />
                  <div className="pacp-slider-range">
                    <span>$100</span>
                    <span>$25,000</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalBudget > 0 && (
        <div className="pacp-budget-summary-container">
          <div className="total-budget-container">
            <span className="budget-label">Paid Advertisement Budget:</span>
            <span className="budget-value">{formatCurrency(totalBudget)}</span>
          </div>
          <div className="media-planning-fees-container">
            <span className="budget-label">Media Planning Fees:</span>
            <span className="budget-value">{formatCurrency(mediaplanFees)}</span>
          </div>
          <div className="subtotal-container">
            <span className="budget-label">Sub-Total:</span>
            <span className="budget-value">{formatCurrency(totalBudget + mediaplanFees)}</span>
          </div>
          <div className="pads-action-buttons-container">
            <button className="pads-add-to-cart" onClick={handleAddToCart}>Add to Cart</button>
            <button className="pads-buy-now" onClick={handleBuyNow}>Buy Now</button>
          </div>
        </div>
      )}
    </div>
  );
}
